import { MarketUnit } from '@easy-lib/modules/shared/models';
import { Side } from '../../order/oms/models/orderModels.model';
import { ContractConfirmStateType } from '@easy-lib/modules/user-agreements/contract';

export interface Position {
	id: string;
	symbolIsin: string;
	symbolName: string;
	side: Side;
	executedQuantity: number;
	totalMargin: number;
	openBuyQuantity: number;
	openSellQuantity: number;
	cashSettlement: CashSettlement;
	physicalSettlement: PhysicalSettlement;
	//...
}
export interface PositionResponse {
	id: string;
	symbolIsin: string;
	symbolName: string;
	side: Side;
	executedQuantity: 1;
	totalMargin: number;
	openBuyQuantity: number;
	openSellQuantity: number;
	cashSettlement: CashSettlement;
	physicalSettlement: PhysicalSettlement;
}
export interface OptionCustomerStatus {
	status: CustomerStatusType;
	statusExtra: { title: string; class: string };
	margin: number;
}
export enum CustomerStatusType {
	Unknown = -1,
	Normal = 0,
	Danger = 1,
	CallMargin = 2
}
export const OptionCustomerExtraState: { [key in CustomerStatusType]: { title: string; class: string } } = {
	[CustomerStatusType.Unknown]: { title: 'نامشخص', class: 'text-body' },
	[CustomerStatusType.Normal]: { title: 'عادی', class: 'text-success' },
	[CustomerStatusType.Danger]: { title: 'در خطر', class: 'text-warning' },
	[CustomerStatusType.CallMargin]: { title: 'کال مارجین', class: 'text-danger' }
};

export interface ColumnState {
	colId: string;
	colName: string;
	colVisibility: boolean;
	colPinability: boolean;
}
export interface PositionItem extends Position {
	symbolName: string;
	stateColor: string;
	strikePrice: number;
	shareDueDate: Date;
	currentValue: number;
	lastTradedPrice: number;
	lastTradedPricePercent: number;
	closingPrice: number;
	closingPricePercent: number;
	todayProfit: number;
	todayProfitPercent: number;
	cashSettlement: CashSettlement;
	physicalSettlement: PhysicalSettlement;
	canBuy: boolean;
	canSell: boolean;
}
export interface PositionSummary {
	totalMargin: number;
	totalCurrentValue: number;
}
export enum OptionTab {
	Position = 1,
	Chain = 2,
	MarketView = 3,
	MarketMap = 4
}
export enum ContractStatus {
	Gain = 'در سود',
	Loss = 'در زیان',
	Even = 'بی تفاوت'
}
export interface PositionHistoryResponse {
	records: PositionHistoryItem[];
	totalRecords: number;
}
export interface PositionHistoryItem {
	id: string;
	customerIsin: string;
	symbolIsin: string;
	symbolName: string;
	executedMargin: number;
	side: Side;
	executedQuantity: number;
	sumMarginableQuantity: number;
	openSellQuantity: number;
	openBuyQuantity: number;
	contractId: string;
	initialMargin: number;
	requiredMargin: number;
	maxCOP: number;
	maxCAOP: number;
	contractStartDate: Date;
	contractEndDate: Date;
	contractSize: number;
	cashSettlementDate: Date;
	physicalSettlementDate: Date;
	totalMargin: number;
	cashSettlement: CashSettlement;
	physicalSettlement: PhysicalSettlement;
	orders: PositionOrder[];
	ordersCount: number;
}
export interface PositionOrder {
	id: string;
	positionId: string;
	contractInitialMargin: number;
	contractRequiredMargin: number;
	unitPrice: number;
	requestedQuantity: number;
	totalPrice: number;
	openQuantityMargin: number;
	marginableQuantity: number;
	temporaryMarginableQuantity: null;
	openQuantity: number;
	executedQuantity: number;
	canceledQuantity: number;
	premium: number;
	orderStrategy: number;
	side: number;
	validityType: number;
	validityDate?: Date;
}

export interface CashSettlement {
	id: string;
	isDeleted: boolean;
	state: SettlementState;
	quantity: number;
	maximumSending: boolean;
	positionId: string;
	omsMoneyChangeOperationId: string;
	moneyChangeAmount: number;
	fraction: boolean;
	cashSettlementMargin: number;
	cashSettlementConfirmDate: Date;
	amountExecutedInCash: number;
	amountNotExecutedInCash: number;
	createDate: Date;
}
export interface PhysicalSettlement {
	id: string;
	isDeleted: boolean;
	state: SettlementState;
	quantity: number;
	maximumSending: boolean;
	positionId: string;
	omsMoneyChangeOperationId: string;
	moneyChangeAmount: number;
	requestSettlementInLoss: boolean;
	physicalSettlementMargin: number;
	physicalSettlementConfirmDate: Date;
	amountExecutedInPhysical: number;
	amountNotExecutedInPhysical: number;
	createDate: Date;
}

export interface SettlementRequest {
	quantity: number;
	sendingMaximum: boolean;
	symbolIsin: string;
}
export interface CashSettlementRequest extends SettlementRequest {
	fraction: boolean;
}
export interface PhysicalSettlementRequest extends SettlementRequest {
	requestSettlementInLoss: boolean;
}
export const enum SettlementState {
	NoRequest,
	SubmittedByUser,
	SubmittedBySystem,
	SentToCore,
	CashSettlementConfirmed,
	CashSettlementConfirmFailed,
	PhysicalSettlementConfirmed,
	ProfitError,
	MoneyError
}

export const CashSettlementTitle: { [key in SettlementState]: string } = {
	[SettlementState.NoRequest]: 'درخواستی ثبت نشده',
	[SettlementState.SubmittedByUser]: 'ثبت شده توسط کاربر',
	[SettlementState.SubmittedBySystem]: 'ثبت شده توسط سیستم',
	[SettlementState.SentToCore]: 'ارسال شده به هسته',
	[SettlementState.CashSettlementConfirmed]: 'انجام شده',
	[SettlementState.CashSettlementConfirmFailed]: 'انجام نشده',
	[SettlementState.PhysicalSettlementConfirmed]: 'انجام شده',
	[SettlementState.ProfitError]: 'قرارداد در زیان است',
	[SettlementState.MoneyError]: 'عدم مانده کافی'
};
export const OptionSide: Record<number, string> = {
	[Side.Buy]: 'buy',
	[Side.Sell]: 'sell'
};
export enum SettlementType {
	CashSettlement = 1,
	PhysicalSettlement
}
export type PositionExportType = {
	title?: string;
	fieldName?: string;
	value?: any;
	width?: number;
};
export const CashSettlementExport: PositionExportType[] = [
	{
		title: 'تعداد درخواست برای تسویه',
		fieldName: 'quantity'
	},
	{
		title: 'اعمال حداکثری',
		fieldName: 'maximumSending'
	},
	{
		title: 'موافقت با تسویه به کسر',
		fieldName: 'fraction'
	},
	{
		title: 'تاریخ',
		fieldName: 'createDate',
		width: 20
	}
];
export const PhysicalSettlementExport: PositionExportType[] = [
	{
		title: 'تعداد درخواست برای تسویه',
		fieldName: 'quantity'
	},
	{
		title: 'اعمال حداکثری',
		fieldName: 'maximumSending'
	},
	{
		title: 'موافقت با تسویه در زیان',
		fieldName: 'requestSettlementInLoss'
	},
	{
		title: 'تاریخ',
		fieldName: 'createDate',
		width: 20
	}
];
export const OptionChainExport: PositionExportType[] = [
	{
		title: 'نماد',
		fieldName: 'symbolName',
		width: 15
	},
	{
		title: 'نوع اوراق',
		fieldName: 'optionSide'
	},

	{
		title: 'وضعیت قرارداد',
		fieldName: 'contractState',
		width: 15
	},
	{
		title: 'قیمت تقاضا',
		fieldName: 'bestBuyPrice',
		width: 15
	},
	{
		title: 'قیمت عرضه',
		fieldName: 'bestSellPrice',
		width: 15
	},
	{
		title: 'قیمت پایانی',
		fieldName: 'closingPrice',
		width: 15
	},
	{
		title: 'آخرین قیمت',
		fieldName: 'lastTradedPrice',
		width: 15
	},
	{
		title: 'بلک شولز',
		fieldName: 'blackScholesPrice',
		width: 15
	},
	{
		title: 'نوسان‌پذیری ضمنی',
		fieldName: 'impliedVolatility',
		width: 15
	}
];
export const OptionSideFilter: Record<number, string> = {
	0: 'خرید',
	1: 'فروش',
	3: 'هردو'
};

export interface OptionChainItem {
	symbolIsin: string;
	symbolName: string;
	stateColor: string;
	stateCode: string;
	strikePrice: number;
	shareDueDate: Date;
	lastTradedPrice: number;
	lastTradedPricePercent: number;
	closingPrice: number;
	closingPricePercent: number;
	totalNumberOfSharesTraded: number;
	feeOfPreviousDaysClosingPrice: number;
	bestBuyPrice: number;
	bestSellPrice: number;
	bestBuyQuantity: number;
	bestSellQuantity: number;
	totalTradeValue: number;
	blackScholes: number;
	marketUnit: MarketUnit;
	contractState: number;
	blackScholesPrice: number;
	impliedVolatility: number;
	// optionSide?: string;
}
export const enum ContractState {
	InProfit = 0,
	InLoss
}
export type OptionChainFilter = {
	date: Date;
	optionSide: Side;
	optionUnits: OptionMarketUnitType[] | undefined;
	componyIsin: string;
};
export const BuyOptionMarketUnit = {
	[MarketUnit.BuyOptiontse]: MarketUnit.BuyOptiontse,
	[MarketUnit.BuyOptionifb]: MarketUnit.BuyOptionifb
};
export const SellOptionMarketUnit = {
	[MarketUnit.SellOptiontse]: MarketUnit.SellOptiontse,
	[MarketUnit.SellOptionifb]: MarketUnit.SellOptionifb
};
export const OptionMarketUnit = { ...BuyOptionMarketUnit, ...SellOptionMarketUnit };
export type OptionMarketUnitType = keyof typeof OptionMarketUnit;

export const OptionUnitSide: { [key in OptionMarketUnitType]: Side } = {
	'45': Side.Buy,
	'46': Side.Buy,
	'47': Side.Sell,
	'48': Side.Sell
};
export type OptionSideFilter = { key: number | string; title: string };
export type BaseSymbolTrade = { symbolIsin: string; price: number; pricePercent: number };
export const SelectOptionUnit: Record<0 | 1, number[]> = {
	0: [MarketUnit.BuyOptiontse, MarketUnit.BuyOptionifb],
	1: [MarketUnit.SellOptiontse, MarketUnit.SellOptionifb]
};
export const OptionContractStateNotif: { [key in ContractConfirmStateType]: { title: string; description: string } } = {
	[ContractConfirmStateType.NotRequested]: {
		title: 'شما به بازار اختیار معامله دسترسی ندارید!',
		description: 'برای ایجاد دسترسی ابتدا می‌بایست قرارداد اختیار معامله را امضا کنید'
	},
	[ContractConfirmStateType.PendingForReview]: {
		title: 'درخواست شما در حال بررسی‌ می‌باشد',
		description: 'مشتری گرامی درخواست دسترسی شما به سرویس اختیار معاملات توسط کارشناسان ما در حال بررسی‌ می‌باشد'
	},
	[ContractConfirmStateType.Rejected]: {
		title: 'شما به بازار اختیار معامله دسترسی ندارید',
		description: 'مشتری گرامی درخواست دسترسی شما به سرویس اختیار معاملات توسط کارشناسان ما رد شده است'
	},
	[ContractConfirmStateType.Confirm]: undefined
};
